import axios from 'axios'

// In production, use the Render backend. In development, use local proxy.
const API_BASE = import.meta.env.VITE_API_BASE ||
  (import.meta.env.PROD ? 'https://orgpulse-md0r.onrender.com' : '')

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  // Enable cookies for httpOnly token authentication
  withCredentials: true,
  // Timeout after 30 seconds to prevent hanging requests
  timeout: 30000,
})

// Request interceptor to add auth token and organization header
api.interceptors.request.use(
  (config) => {
    // Auth token from localStorage (fallback for cross-origin when cookies don't work)
    const token = localStorage.getItem('token')
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    // Organization ID header
    const org = localStorage.getItem('team') || localStorage.getItem('organization')
    if (org) {
      try {
        const orgData = JSON.parse(org)
        config.headers['X-Organization-ID'] = orgData.id
      } catch {
        // Invalid JSON, ignore
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear local state on unauthorized
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('team')
      localStorage.removeItem('organization')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Campaign API
export const campaignApi = {
  list: () => api.get('/api/campaigns'),
  get: (id) => api.get(`/api/campaigns/${id}`),
  create: (data) => api.post('/api/campaigns', data),
  update: (id, data) => api.patch(`/api/campaigns/${id}`, data),
  delete: (id) => api.delete(`/api/campaigns/${id}`),
  getStats: (id) => api.get(`/api/campaigns/${id}/stats`),

  // Members
  getMembers: (id) => api.get(`/api/campaigns/${id}/members`),
  addMember: (id, data) => api.post(`/api/campaigns/${id}/members`, data),
  importMembers: (id, file) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/api/campaigns/${id}/members/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  importMappedMembers: (id, members) =>
    api.post(`/api/campaigns/${id}/members/import-mapped`, members),
  deleteMember: (campaignId, memberId) =>
    api.delete(`/api/campaigns/${campaignId}/members/${memberId}`),

  // Chat
  getChat: (id, chatType = 'setup') => api.get(`/api/campaigns/${id}/chat?chat_type=${chatType}`),
  deleteChat: (id, chatType = 'setup') =>
    api.delete(`/api/campaigns/${id}/chat?chat_type=${chatType}`),

  // Invitations (10 minute timeout for demo mode which generates AI interviews)
  sendInvitations: (id, memberIds = null) =>
    api.post(`/api/campaigns/${id}/invitations/send`, { member_ids: memberIds }, { timeout: 600000 }),

  // Embeddings
  getEmbeddingStats: (id) => api.get(`/api/campaigns/${id}/embeddings/stats`),
  generateEmbeddings: (id) => api.post(`/api/campaigns/${id}/embeddings/generate`),

  // Insights
  getInsightsStats: (id) => api.get(`/api/campaigns/${id}/insights/stats`),
  searchInsights: (id, query, filters = {}) =>
    api.post(`/api/campaigns/${id}/insights/search`, { query, ...filters }),

  // Reports
  generateReport: (id) => api.post(`/api/campaigns/${id}/report/generate`),
  getReport: (id) => api.get(`/api/campaigns/${id}/report`),
  downloadReportPdf: (id) =>
    api.get(`/api/campaigns/${id}/report/pdf`, { responseType: 'blob' }),
  createShareLink: (id, data = {}) =>
    api.post(`/api/campaigns/${id}/report/share`, data),
  disableShareLink: (id) => api.delete(`/api/campaigns/${id}/report/share`),
}

// Interview API (public)
export const interviewApi = {
  validate: (token) => api.get(`/api/invitations/${token}/validate`),
  consent: (token) => api.post(`/api/invitations/${token}/consent`, { consent_given: true }),
  getSession: (id) => api.get(`/api/interviews/${id}/session`),
  complete: (id) => api.post(`/api/interviews/${id}/complete`),
}

// Organization API
export const organizationApi = {
  list: () => api.get('/api/organizations'),
  getMembers: (orgId) => api.get(`/api/organizations/${orgId}/members`),
  addMember: (orgId, data) => api.post(`/api/organizations/${orgId}/members`, data),
  updateMember: (orgId, userId, data) =>
    api.patch(`/api/organizations/${orgId}/members/${userId}`, data),
  removeMember: (orgId, userId) =>
    api.delete(`/api/organizations/${orgId}/members/${userId}`),

  // Invites
  getInvites: (orgId) => api.get(`/api/organizations/${orgId}/invites`),
  createInvite: (orgId, data) => api.post(`/api/organizations/${orgId}/invites`, data),
  cancelInvite: (orgId, inviteId) =>
    api.delete(`/api/organizations/${orgId}/invites/${inviteId}`),

  // Campaign Credits
  getCampaignCredits: () => api.get('/api/organizations/campaign-credits'),
}

// Invite API (for accepting invites)
export const inviteApi = {
  getInfo: (token) => api.get(`/api/invites/${token}`),
  accept: (token) => api.post(`/api/invites/${token}/accept`),
}

// Assessment API (public)
export const assessmentApi = {
  submit: (data) => api.post('/api/leads/assessment', data),
}

// Public Report API (no auth required)
export const publicReportApi = {
  get: (token) => api.get(`/api/reports/${token}`),
  accessWithPassword: (token, password) =>
    api.post(`/api/reports/${token}`, { password }),
}

// Demo API (for demo account login)
export const demoApi = {
  login: () => api.post('/api/demo/login'),
  status: () => api.get('/api/demo/status'),
}

// Admin API (super admin dashboard)
export const adminApi = {
  // Organizations
  listOrganizations: (skip = 0, limit = 100) =>
    api.get(`/api/admin/organizations?skip=${skip}&limit=${limit}`),
  getOrganization: (orgId) => api.get(`/api/admin/organizations/${orgId}`),
  updateOrganization: (orgId, data) => api.patch(`/api/admin/organizations/${orgId}`, data),
  resetStuckSimulations: (orgId) => api.post(`/api/admin/organizations/${orgId}/reset-stuck-simulations`),

  // Campaigns
  listCampaigns: (skip = 0, limit = 100, orgId = null) => {
    const params = new URLSearchParams({ skip, limit })
    if (orgId) params.append('org_id', orgId)
    return api.get(`/api/admin/campaigns?${params}`)
  },
  getCampaign: (campaignId) => api.get(`/api/admin/campaigns/${campaignId}`),
  updateCampaign: (campaignId, data) => api.patch(`/api/admin/campaigns/${campaignId}`, data),
  deleteCampaign: (campaignId) => api.delete(`/api/admin/campaigns/${campaignId}?confirm=true`),

  // Users
  listUsers: (skip = 0, limit = 100) =>
    api.get(`/api/admin/users?skip=${skip}&limit=${limit}`),
  toggleSuperadmin: (userId, isSuperadmin) =>
    api.patch(`/api/admin/users/${userId}/superadmin`, { is_superadmin: isSuperadmin }),

  // Partners
  listPartners: (skip = 0, limit = 100, status = null) => {
    const params = new URLSearchParams({ skip, limit })
    if (status) params.append('status', status)
    return api.get(`/api/admin/partners?${params}`)
  },
  getPartner: (partnerId) => api.get(`/api/admin/partners/${partnerId}`),
  updatePartner: (partnerId, data) => api.patch(`/api/admin/partners/${partnerId}`, data),
  approvePartner: (partnerId) => api.post(`/api/admin/partners/${partnerId}/approve`),
  suspendPartner: (partnerId) => api.post(`/api/admin/partners/${partnerId}/suspend`),

  // Payouts
  listPayouts: (skip = 0, limit = 100, status = null) => {
    const params = new URLSearchParams({ skip, limit })
    if (status) params.append('status', status)
    return api.get(`/api/admin/payouts?${params}`)
  },
  updatePayout: (payoutId, data) => api.patch(`/api/admin/payouts/${payoutId}`, data),
  processPayout: (payoutId, payoutReference = null) => {
    const params = payoutReference ? `?payout_reference=${encodeURIComponent(payoutReference)}` : ''
    return api.post(`/api/admin/payouts/${payoutId}/process${params}`)
  },

  // Partner Revenue
  listPartnerRevenue: (skip = 0, limit = 100, status = null, partnerId = null) => {
    const params = new URLSearchParams({ skip, limit })
    if (status) params.append('status', status)
    if (partnerId) params.append('partner_id', partnerId)
    return api.get(`/api/admin/partner-revenue?${params}`)
  },
  approveRevenue: (revenueId) => api.patch(`/api/admin/partner-revenue/${revenueId}/approve`),

  // Campaign Credits
  giftCampaign: (data) => api.post('/api/admin/gift-campaign', data),
  getOrganizationCredits: (orgId) => api.get(`/api/admin/organizations/${orgId}/credits`),
}

// Streaming helper for SSE endpoints (axios doesn't support streams)
// This maintains the same auth logic as the axios interceptors
export const streamFetch = async (url, options = {}) => {
  // Build headers with auth token and org ID
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  // Add auth token
  const token = localStorage.getItem('token')
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  // Add organization ID
  const org = localStorage.getItem('team') || localStorage.getItem('organization')
  if (org) {
    try {
      const orgData = JSON.parse(org)
      headers['X-Organization-ID'] = orgData.id
    } catch {
      // Invalid JSON, ignore
    }
  }

  const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`

  const response = await fetch(fullUrl, {
    ...options,
    headers,
    credentials: 'include', // Match axios withCredentials
  })

  // Handle auth errors like the axios interceptor
  if (response.status === 401) {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('team')
    localStorage.removeItem('organization')
    window.location.href = '/login'
    throw new Error('Unauthorized')
  }

  // Check for other errors
  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}: ${response.statusText}`)
    error.response = response
    throw error
  }

  return response
}

export default api
